// UGC / shoppable-video / agentic-commerce glossary.
//
// May 2026 expansion: ~125 entries across 8 topic clusters, each
// entry enriched with aliases, disambiguation, example sentence,
// cluster + colour, related glossary terms, supporting articles,
// downloads, and industry pages where the term matters most.
//
// Renders at:
//   /glossary            -> listing page with search + cluster filter
//   /glossary/[slug]     -> per-term page (cited individually by AI)
//
// Schema emitted per page:
//   DefinedTermSet (listing)
//   DefinedTerm + FAQPage + Article + Speakable (per-term)
//   mentions array binding each term to its supporting articles + pages

export type GlossaryCluster =
  | 'ugc-ops'
  | 'shoppable-video'
  | 'aeo'
  | 'legal'
  | 'metrics'
  | 'platforms'
  | 'platform-ui'
  | 'vertical'

export type GlossaryEntry = {
  /** URL slug and DefinedTerm @id. Stable; never rename in place. */
  slug: string
  /** Display name, also the searchable lookup. */
  term: string
  /** Topic cluster, drives the filter chip + accent colour. */
  cluster: GlossaryCluster
  /** AI-extractable 1-2 sentence definition. Marked Speakable. */
  shortAnswer: string
  /** Optional 2-4 sentence expansion. */
  longBody?: string
  /** Other names this term is commonly called. Surface as pills. */
  aliases?: string[]
  /** "Often confused with..." disambiguation. */
  notTheSameAs?: { term: string; reason: string }[]
  /** A sentence using the term in a realistic context. */
  example?: string
  /** Cross-links to articles that go deep on this term. */
  relatedSlugs?: string[]
  /** Related glossary entries (intra-glossary linking). */
  relatedTerms?: string[]
  /** Industry-page slugs where this term is relevant. */
  industries?: string[]
  /** Download slugs (from lib/copy/downloads.ts) attached to this term. */
  downloads?: string[]
  /** Wikipedia / schema.org / official authority URL for `sameAs`. */
  sameAs?: string
  /** Optional pronunciation hint, e.g. "U-G-C". */
  pronunciation?: string
}

export const GLOSSARY_CLUSTERS: { id: GlossaryCluster; label: string; description: string }[] = [
  { id: 'ugc-ops',          label: 'UGC operations',     description: 'Collection, rights, moderation and distribution workflows.' },
  { id: 'shoppable-video',  label: 'Shoppable video',    description: 'Video formats, hotspots, live commerce and add-to-cart mechanics.' },
  { id: 'aeo',              label: 'AEO and AI search',  description: 'Generative engines, agentic commerce, structured data and citation signals.' },
  { id: 'legal',            label: 'Legal and compliance', description: 'GDPR, FTC, CCPA, image rights, consent and revocation.' },
  { id: 'metrics',          label: 'Metrics',            description: 'Conversion, RPV, CWV, lift, freshness and the denominators that matter.' },
  { id: 'platforms',        label: 'Platforms',          description: 'Ecommerce, social and review platforms Idukki integrates with.' },
  { id: 'platform-ui',      label: 'Social formats',     description: 'Reel, post, carousel, story, pin. The native shapes social content arrives in.' },
  { id: 'vertical',         label: 'Industry-specific',  description: 'Fit-checks, swatches, unboxings, real-room photos. The language of each vertical.' },
]

export const GLOSSARY: GlossaryEntry[] = [
  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 1, UGC OPERATIONS
  // ═══════════════════════════════════════════════════════════════
  {
    slug: 'ugc',
    term: 'UGC (user-generated content)',
    cluster: 'ugc-ops',
    pronunciation: 'U-G-C',
    aliases: ['user-generated content', 'customer content', 'community content'],
    shortAnswer:
      'UGC is any photo, video, review, or social post about a product created by a customer rather than the brand. In ecommerce it is used as on-site social proof and as creative for paid ads.',
    longBody:
      'UGC sits on the spectrum between brand-produced creative (high control, high cost) and influencer content (paid, semi-structured). It is the most credible content type for conversion-stage decisions because shoppers weight peer experience higher than brand claims. Programmes that operationalise UGC across collection, rights, distribution and measurement see a median 18% PDP conversion lift in our 2,400-brand dataset.',
    example: 'We pulled 14 customer fit-check Reels into the PDP gallery and PDP conversion lifted 22% that quarter.',
    notTheSameAs: [
      { term: 'Influencer content', reason: 'Influencer content is paid or gifted; UGC is unpaid and unprompted.' },
      { term: 'Brand-produced content', reason: 'Brand-produced is shot by the brand or its agency; UGC is shot by the buyer.' },
    ],
    relatedSlugs: ['what-is-ugc-ecommerce', 'ugc-strategy-framework', 'state-of-ugc-2026-report'],
    relatedTerms: ['ugc-rights-management', 'social-proof', 'verified-buyer', 'earned-content'],
    industries: ['fashion-and-apparels', 'beauty-and-cosmetics', 'home-lifestyle'],
    downloads: ['ai-in-the-ugc-loop-series'],
    sameAs: 'https://en.wikipedia.org/wiki/User-generated_content',
  },
  {
    slug: 'ugc-rights-management',
    term: 'UGC rights management',
    cluster: 'ugc-ops',
    aliases: ['rights workflow', 'permission management', 'consent capture'],
    shortAnswer:
      'UGC rights management is the workflow of requesting, recording and revoking permission to reuse customer content commercially. A valid record names the asset, the scope of use, the duration and the creator\'s unambiguous yes.',
    longBody:
      'Manual rights tracking scales to roughly 50 pieces per month per FTE. Above that, automated workflows with one-tap consent forms and audit-trail storage are essential. The yes-rate gap between brands is enormous: well-run programmes hit 50-60%; ad-hoc programmes hit 8-15%. GDPR requires revocation to be honoured within roughly 30 days end-to-end including ad-cache and CDN.',
    example: 'After we moved the consent ask to a one-tap form, yes-rate jumped from 24% to 38% on the same templates.',
    relatedSlugs: ['how-to-get-ugc-rights', 'what-is-ugc-rights-management', 'gdpr-ugc-compliance'],
    relatedTerms: ['affirmative-consent', 'right-to-be-forgotten', 'consent-record', 'gdpr'],
    industries: ['fashion-and-apparels', 'beauty-and-cosmetics'],
  },
  {
    slug: 'social-proof',
    term: 'Social proof',
    cluster: 'ugc-ops',
    aliases: ['peer evidence', 'crowd evidence'],
    shortAnswer:
      'Social proof is the conversion lift a shopper takes from seeing other people validate a product. Reviews, photos, videos, "X people just bought this" and "Y customers rated this 4.7" are all social-proof formats.',
    longBody:
      'The mechanism is Cialdini-classic and the magnitude on ecommerce PDPs is well-instrumented. PDPs with reviews see roughly 354% higher conversion than PDPs without (Bazaarvoice 2025 SEI). UGC photos amplify the effect by adding contextual demonstration on top of the trust signal.',
    relatedSlugs: ['social-proof-psychology', 'what-is-ugc-ecommerce'],
    relatedTerms: ['ugc', 'verified-buyer'],
    sameAs: 'https://en.wikipedia.org/wiki/Social_proof',
  },
  {
    slug: 'verified-buyer',
    term: 'Verified buyer',
    cluster: 'ugc-ops',
    aliases: ['verified purchaser', 'isVerifiedBuyer'],
    shortAnswer:
      'A verified buyer is a reviewer whose purchase has been chain-of-custody confirmed by the platform (order ID matched). AI shopping agents weight verified-buyer reviews roughly 14x more than unverified content when assembling shortlists.',
    longBody:
      'Mark verified buyers in schema.org with the `isVerifiedBuyer: true` field on each Review. The field is the single highest-leverage AEO signal on a PDP after AggregateRating itself.',
    example: 'We added isVerifiedBuyer flags across the review feed and saw a 4x lift in Perplexity citations within six weeks.',
    relatedSlugs: ['answer-engine-optimisation-2026-playbook', 'state-of-ugc-2026-report'],
    relatedTerms: ['aggregate-rating', 'photo-review', 'video-review'],
  },
  {
    slug: 'earned-content',
    relatedSlugs: ['what-is-ugc-and-why-it-converts', 'how-to-get-more-ugc-without-paying', 'ugc-vs-brand-produced'],
    term: 'Earned content',
    cluster: 'ugc-ops',
    aliases: ['organic UGC', 'unprompted UGC'],
    shortAnswer:
      'Earned content is UGC that arrived without a brief, payment or campaign trigger. The customer chose to post, tagged the brand, and the brand earned the mention. Highest-credibility tier of UGC.',
    notTheSameAs: [
      { term: 'Paid UGC', reason: 'Paid UGC is briefed and paid; earned content is voluntary.' },
      { term: 'Gifted UGC', reason: 'Gifted content involved a brand-funded product send; earned did not.' },
    ],
    relatedTerms: ['ugc', 'influencer-content-vs-ugc', 'branded-hashtag'],
  },
  {
    slug: 'branded-hashtag',
    relatedSlugs: ['how-to-run-a-ugc-hashtag-campaign', 'hashtag-campaign-ugc', 'hashtag-to-pdp-28-day-field-study'],
    term: 'Branded hashtag',
    cluster: 'ugc-ops',
    aliases: ['campaign hashtag', 'community hashtag'],
    shortAnswer:
      'A branded hashtag is a brand-owned tag (e.g. #MyWestElm) used to aggregate inbound UGC. The right one is short, distinctive, easy to type on mobile, and not already in heavy non-brand use.',
    example: 'We launched #LiveInIdukki and pulled 1,200 tagged posts in the first 30 days.',
    relatedTerms: ['inbound-capture', 'hashtag-tournament', 'ugc'],
  },
  {
    slug: 'hashtag-tournament',
    relatedSlugs: ['how-to-run-a-ugc-competition', 'how-to-run-a-ugc-hashtag-campaign', 'hashtag-campaign-ugc'],
    term: 'Hashtag tournament',
    cluster: 'ugc-ops',
    aliases: ['UGC competition', 'submission contest'],
    shortAnswer:
      'A time-boxed UGC competition with a public or jury vote at the end. Produces 10-12x the inbound volume of an always-on hashtag during the campaign window.',
    relatedTerms: ['branded-hashtag', 'ugc'],
    industries: ['fashion-and-apparels'],
  },
  {
    slug: 'auto-dm-consent-flow',
    term: 'Auto-DM consent flow',
    cluster: 'ugc-ops',
    aliases: ['automated rights request', 'scripted DM'],
    shortAnswer:
      'An automated DM sent to a creator within 24 hours of their post going up, laying out commercial-use scope and capturing affirmative consent via a one-tap form. The standard flow for any UGC programme above 50 pieces per month.',
    relatedSlugs: ['how-to-get-ugc-rights'],
    relatedTerms: ['ugc-rights-management', 'affirmative-consent', 'yes-rate'],
  },
  {
    slug: 'photo-review',
    term: 'Photo review',
    cluster: 'ugc-ops',
    shortAnswer:
      'A review that includes a customer-submitted photo of the product. Converts 2.6x better than text-only reviews on the same SKU (PowerReviews, 2023 baseline).',
    relatedSlugs: ['review-volume-benchmarks', 'state-of-ugc-2026-report'],
    relatedTerms: ['video-review', 'verified-buyer', 'ugc'],
  },
  {
    slug: 'video-review',
    term: 'Video review',
    cluster: 'ugc-ops',
    shortAnswer:
      'A review with an embedded customer-submitted video clip. Converts 4.1x better than text-only reviews on the same SKU. Hardest UGC category to scale operationally (rights, hosting, moderation overhead).',
    relatedSlugs: ['shoppable-video-vs-photography'],
    relatedTerms: ['shoppable-video', 'photo-review', 'verified-buyer'],
  },
  {
    slug: 'ugc-moderation',
    term: 'UGC moderation',
    cluster: 'ugc-ops',
    aliases: ['moderation queue', 'approval queue'],
    shortAnswer:
      'The pre-publish queue plus filters that decide what UGC ships on-site. Three tiers: auto-approve safe content, auto-reject blocked content, queue ambiguous content for a human.',
    relatedSlugs: ['ugc-moderation-best-practices'],
    relatedTerms: ['brand-safety-filter', 'ugc-rights-management'],
  },
  {
    slug: 'brand-safety-filter',
    relatedSlugs: ['ai-auto-curation-of-ugc', 'moderating-ugc-at-scale', 'ugc-moderation-best-practices'],
    term: 'Brand-safety filter',
    cluster: 'ugc-ops',
    aliases: ['NSFW filter', 'content-safety filter'],
    shortAnswer:
      'An automated check that screens UGC for NSFW imagery, competitor mentions, blocklisted words, and copyright issues before it reaches the moderation queue. Cuts moderator load 80%+ on a well-tuned setup.',
    relatedTerms: ['ugc-moderation', 'music-licensing-in-ugc'],
  },
  {
    slug: 'ugc-library',
    relatedSlugs: ['repurposing-ugc-across-every-channel', 'omnichannel-ugc-consistent-proof', 'build-an-ai-curated-self-updating-gallery'],
    term: 'UGC library / DAM',
    cluster: 'ugc-ops',
    aliases: ['digital asset manager', 'UGC DAM'],
    shortAnswer:
      'The store-once, decorate-for-N-channels asset database that lets the same approved UGC piece power on-site galleries, lifecycle email, paid ads and retail signage without re-uploading.',
    relatedTerms: ['ugc-pipeline', 'rights-cleared-asset'],
  },
  {
    slug: 'lookbook',
    relatedSlugs: ['shop-the-look-from-a-customer-photo', 'shoppable-gallery-guide', 'best-shoppable-video-fashion'],
    term: 'Lookbook',
    cluster: 'ugc-ops',
    shortAnswer:
      'A curated, hotspot-tagged collection of UGC rendered as a flipbook or scrolling editorial page. Best for higher-AOV considered purchases where the shopper benefits from styling context.',
    relatedTerms: ['shoppable-gallery', 'hotspot'],
    industries: ['fashion-and-apparels', 'home-lifestyle', 'furniture'],
  },
  {
    slug: 'ugc-freshness-cadence',
    term: 'UGC freshness cadence',
    cluster: 'ugc-ops',
    aliases: ['gallery refresh', 'rotation cadence'],
    shortAnswer:
      'How often the on-site gallery is refreshed. Weekly refresh outperforms monthly by 1.8x on sustained conversion lift; galleries left stale past 90 days lose roughly a third of their initial lift.',
    relatedSlugs: ['state-of-ugc-2026-report'],
    relatedTerms: ['ugc-pipeline'],
  },
  {
    slug: 'post-purchase-prompt',
    term: 'Post-purchase prompt',
    cluster: 'ugc-ops',
    aliases: ['review request', 'photo request'],
    shortAnswer:
      'An email or SMS sent 7-14 days after delivery asking the buyer for a review and a photo. Photo-incentivised prompts double yes-rate vs unincentivised. Watch FTC disclosure on meaningful incentives.',
    relatedSlugs: ['how-to-get-ugc-rights'],
    relatedTerms: ['yes-rate', 'ftc-endorsement-guidelines'],
  },
  {
    slug: 'inbound-capture',
    term: 'Inbound capture',
    cluster: 'ugc-ops',
    aliases: ['social listening', 'mention listening'],
    shortAnswer:
      'Always-on monitoring across branded hashtags, mentions and tagged posts so new UGC enters the rights queue within hours of being posted (yes-rate halves after 7 days).',
    relatedSlugs: ['ai-ugc-loop-ingestion'],
    relatedTerms: ['branded-hashtag', 'auto-dm-consent-flow'],
  },
  {
    slug: 'rights-cleared-asset',
    relatedSlugs: ['how-to-get-ugc-rights', 'ugc-rights-and-permissions-guide', 'rights-clearance-unsexy-moat'],
    term: 'Rights-cleared asset',
    cluster: 'ugc-ops',
    shortAnswer:
      'A piece of UGC with a documented consent record, defined scope, duration, and revocation endpoint. The only kind of UGC safe to use in paid ads.',
    relatedTerms: ['ugc-rights-management', 'consent-record', 'right-to-be-forgotten'],
  },
  {
    slug: 'creator-handle',
    relatedSlugs: ['how-to-collect-ugc-legally-instagram-tiktok', 'when-a-creator-deletes-the-original-post', 'ftc-endorsement-guidelines'],
    term: 'Creator handle',
    cluster: 'ugc-ops',
    aliases: ['author handle', 'attributable creator'],
    shortAnswer:
      'The platform handle credited as the author of a UGC piece (e.g. @maya.london on Instagram). Every published surface should link back to it as both attribution and trust signal.',
    relatedTerms: ['ugc', 'rights-cleared-asset'],
  },
  {
    slug: 'ugc-pipeline',
    term: 'UGC pipeline',
    cluster: 'ugc-ops',
    aliases: ['UGC flow', 'content pipeline'],
    shortAnswer:
      'The four-stage operational flow that turns scattered social content into attributed revenue: ingest, moderate, tag, distribute. Run weekly. Skip a stage and the programme breaks within a quarter.',
    relatedSlugs: ['ai-ugc-loop-ingestion', 'ugc-strategy-framework'],
    relatedTerms: ['inbound-capture', 'ugc-moderation', 'hotspot-tagging'],
  },
  {
    slug: 'influencer-content-vs-ugc',
    term: 'Influencer content vs UGC',
    cluster: 'ugc-ops',
    aliases: ['UGC vs influencer'],
    shortAnswer:
      'Influencer content is paid and briefed; UGC is unpaid and unprompted. Both have their place but obey different cost models, different disclosure rules, and different conversion mechanics.',
    relatedSlugs: ['ugc-vs-influencer-content'],
    relatedTerms: ['ugc', 'earned-content', 'material-connection-ftc'],
  },

  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 2, SHOPPABLE VIDEO
  // ═══════════════════════════════════════════════════════════════
  {
    slug: 'shoppable-video',
    term: 'Shoppable video',
    cluster: 'shoppable-video',
    aliases: ['interactive video', 'tappable video', 'tagged video'],
    shortAnswer:
      'Shoppable video is video content with embedded, clickable product tags that let viewers add to cart without leaving the player. Lifts PDP conversion by a median +21% over photo-only galleries in considered-purchase categories.',
    example: 'We embedded a 15-second Reel with three hotspots above the price block and time-to-cart dropped from 38s to 11s.',
    relatedSlugs: ['what-is-shoppable-video', 'shoppable-video-vs-photography'],
    relatedTerms: ['hotspot', 'add-to-cart-from-video', 'linear-shoppable', 'interactive-hotspot'],
    industries: ['fashion-and-apparels', 'beauty-and-cosmetics'],
  },
  {
    slug: 'shoppable-gallery',
    term: 'Shoppable gallery',
    cluster: 'shoppable-video',
    aliases: ['shoppable wall', 'tagged gallery'],
    shortAnswer:
      'A grid or carousel of UGC where every tile links to a product, usually via a hotspot pin or full-tile click. The lowest-friction shoppable UGC format and the default for first-time programmes.',
    relatedSlugs: ['shoppable-gallery-guide'],
    relatedTerms: ['shoppable-video', 'hotspot', 'lookbook'],
  },
  {
    slug: 'hotspot',
    relatedSlugs: ['what-is-shoppable-video', 'interactive-video-hotspots-chapters-branching', 'shoppable-gallery-guide'],
    term: 'Hotspot (product hotspot)',
    cluster: 'shoppable-video',
    aliases: ['product pin', 'product tag'],
    shortAnswer:
      'The clickable point overlayed on a video frame or photo that opens a product card when tapped. The single mechanical unit of shoppable UGC.',
    relatedTerms: ['shoppable-video', 'hotspot-tagging', 'variant-resolution'],
  },
  {
    slug: 'hotspot-tagging',
    relatedSlugs: ['ai-content-tagging-for-ugc', 'interactive-video-hotspots-chapters-branching', 'ai-ugc-loop-tagging'],
    term: 'Hotspot tagging',
    cluster: 'shoppable-video',
    aliases: ['product tagging', 'AI tagging'],
    shortAnswer:
      'The labour of binding hotspots to specific SKUs (with the right variant). Manual takes around 12 min per video; AI-assisted with computer vision drops to around 2 min.',
    relatedTerms: ['hotspot', 'variant-resolution', 'ai-tool-calling'],
  },
  {
    slug: 'add-to-cart-from-video',
    relatedSlugs: ['what-is-shoppable-video', 'make-instagram-reels-tiktoks-shoppable-shopify', 'the-video-first-product-page'],
    term: 'Add-to-cart from video',
    cluster: 'shoppable-video',
    shortAnswer:
      'The cart action fired inside a video player when the viewer taps a hotspot, without pausing playback or leaving the page. The mechanical proof that a video is genuinely shoppable.',
    relatedTerms: ['hotspot', 'tap-to-shop', 'shoppable-video'],
  },
  {
    slug: 'live-commerce',
    relatedSlugs: ['how-to-run-a-live-shopping-event', 'live-shopping-vs-on-demand-shoppable-video', 'what-is-shoppable-video'],
    term: 'Live shopping / live commerce',
    cluster: 'shoppable-video',
    aliases: ['live shopping', 'live stream commerce', 'livestream shopping'],
    shortAnswer:
      'Real-time broadcast with synchronous cart actions as products are shown. Conversion rates run 5-15% vs 2-3% for static commerce (McKinsey), but Western audiences are bounded (median 340 concurrent).',
    relatedTerms: ['shoppable-video'],
    sameAs: 'https://en.wikipedia.org/wiki/Live_streaming_e-commerce',
  },
  {
    slug: 'vertical-portrait-video',
    relatedSlugs: ['mobile-first-shoppable-video-design', 'ideal-length-for-a-shoppable-video', 'best-shoppable-video-fashion'],
    term: 'Vertical / portrait video',
    cluster: 'shoppable-video',
    aliases: ['9:16 video', 'portrait shoppable'],
    shortAnswer:
      '9:16 aspect ratio video. Outperforms landscape on mobile PDPs by roughly 1.6x conversion lift on the same content because it matches the shopper\'s native scrolling orientation.',
    relatedTerms: ['shoppable-video', 'reel', 'story'],
  },
  {
    slug: 'linear-shoppable',
    relatedSlugs: ['what-is-shoppable-video', 'ideal-length-for-a-shoppable-video', 'interactive-video-hotspots-chapters-branching'],
    term: 'Linear shoppable',
    cluster: 'shoppable-video',
    aliases: ['timestamped tags', 'scripted tagging'],
    shortAnswer:
      'One video plays through; product tags appear at pre-scripted timestamps. Best for sub-30-second content where products are seen in sequence. Lowest production cost of the shoppable formats.',
    relatedTerms: ['shoppable-video', 'interactive-hotspot'],
  },
  {
    slug: 'interactive-hotspot',
    relatedSlugs: ['interactive-video-hotspots-chapters-branching', 'what-is-shoppable-video', 'best-shoppable-video-fashion'],
    term: 'Interactive hotspot',
    cluster: 'shoppable-video',
    aliases: ['always-on hotspots', 'free-tap shoppable'],
    shortAnswer:
      'Product tags persist throughout the video and the viewer can tap any visible item at any moment. Higher production cost than linear but the conversion lift is correspondingly larger for considered purchases.',
    relatedTerms: ['shoppable-video', 'linear-shoppable', 'hotspot'],
  },
  {
    slug: 'stories-layout',
    relatedSlugs: ['mobile-first-shoppable-video-design', 'make-instagram-reels-tiktoks-shoppable-shopify', 'where-to-place-shoppable-video'],
    term: 'Stories layout',
    cluster: 'shoppable-video',
    aliases: ['vertical stories', 'story carousel'],
    shortAnswer:
      'Vertical sound-on autoplay format borrowed from Instagram/TikTok Stories, with swipe-between-cards interaction and one hotspot per card. Hero-band placement on beauty and skincare PDPs.',
    relatedTerms: ['vertical-portrait-video', 'reel'],
    industries: ['beauty-and-cosmetics'],
  },
  {
    slug: 'reels-layout',
    relatedSlugs: ['make-instagram-reels-tiktoks-shoppable-shopify', 'mobile-first-shoppable-video-design', 'instagram-vs-tiktok-ugc'],
    term: 'Reels layout',
    cluster: 'shoppable-video',
    shortAnswer:
      'Short-form portrait loop format, TikTok or Instagram-Reels style. Sub-15-second clips with on-screen captions, sound-on, tap-to-shop pinned to the corner.',
    relatedTerms: ['vertical-portrait-video', 'reel', 'shoppable-video'],
  },
  {
    slug: 'tap-to-shop',
    relatedSlugs: ['what-is-shoppable-video', 'the-video-first-product-page', 'shoppable-gallery-guide'],
    term: 'Tap-to-shop',
    cluster: 'shoppable-video',
    aliases: ['click-to-buy', 'one-tap purchase'],
    shortAnswer:
      'The single-gesture purchase action from any shoppable surface (gallery, video, lookbook). Reduces friction by collapsing discovery and cart-action into one tap.',
    relatedTerms: ['add-to-cart-from-video', 'shoppable-video'],
  },
  {
    slug: 'autoplay-muted',
    relatedSlugs: ['mobile-first-shoppable-video-design', 'core-web-vitals-ugc-widgets', 'accessibility-in-shoppable-video'],
    term: 'Autoplay-muted (auto-cap)',
    cluster: 'shoppable-video',
    aliases: ['muted autoplay', 'auto-cap'],
    shortAnswer:
      'Browser-spec compliant auto-start: video starts playing on load with audio muted, sound is opt-in via a tap. The only autoplay form Chrome and Safari permit on mobile.',
    relatedTerms: ['shoppable-video', 'core-web-vitals'],
  },
  {
    slug: 'inline-player',
    relatedSlugs: ['mobile-first-shoppable-video-design', 'inline-vs-floating-widgets', 'where-to-place-shoppable-video'],
    term: 'Inline player',
    cluster: 'shoppable-video',
    aliases: ['embedded player', 'on-page player'],
    shortAnswer:
      'Video player rendered in-stream on the PDP (vs modal popup or full-screen takeover). Higher engagement on mobile because the user doesn\'t lose page context to interact.',
    relatedTerms: ['shoppable-video'],
  },
  {
    slug: 'variant-resolution',
    relatedSlugs: ['interactive-video-hotspots-chapters-branching', 'anatomy-conversational-pdp-nine-components', 'what-is-shoppable-video'],
    term: 'Variant resolution',
    cluster: 'shoppable-video',
    shortAnswer:
      'The engineering of binding a hotspot to the right product variant (size, colour, scent) so the add-to-cart fires without a second click. Most shoppable-video implementations get this wrong on launch and conversion lift halves.',
    relatedTerms: ['hotspot-tagging', 'add-to-cart-from-video'],
  },

  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 3, AEO + AI SEARCH
  // ═══════════════════════════════════════════════════════════════
  {
    slug: 'aeo',
    term: 'AEO (Answer Engine Optimisation)',
    cluster: 'aeo',
    pronunciation: 'A-E-O',
    aliases: ['answer engine optimization', 'generative SEO', 'GEO'],
    shortAnswer:
      'AEO is the practice of preparing pages for citation by AI engines (ChatGPT, Claude, Perplexity, Gemini). The mechanics differ from SEO: engines extract 30-700 token snippets and quote them verbatim, so single-source-of-truth definitions and structured data dominate.',
    longBody:
      'The AEO playbook centres on: machine-readable identity (schema.org), single canonical definitions (no near-duplicates), Speakable markup for voice, FAQPage for "what is X" snippets, verified-buyer review flags, and a clean llms.txt that gives agents a content map. Idukki\'s AEO Scorecard rates 42 signals across 7 categories.',
    notTheSameAs: [
      { term: 'SEO', reason: 'SEO optimises for blue links in a ranked list. AEO optimises for being cited inside a composed answer.' },
    ],
    relatedSlugs: ['answer-engine-optimisation-2026-playbook', 'the-42-signal-aeo-scorecard'],
    relatedTerms: ['agentic-commerce', 'llms-txt', 'speakable-schema', 'citation-graph'],
    downloads: ['aeo-scorecard-csv'],
  },
  {
    slug: 'agentic-commerce',
    term: 'Agentic commerce',
    cluster: 'aeo',
    aliases: ['agent commerce', 'AI commerce'],
    shortAnswer:
      'The class of transactions in which an autonomous software agent, acting on a human goal, discovers candidate products, evaluates them, and either presents a shortlist or completes the purchase with delegated payment.',
    relatedSlugs: ['what-is-agentic-commerce-definition-examples', 'the-agentic-commerce-stack-reference-architecture'],
    relatedTerms: ['ai-shopper', 'mcp', 'merchant-mcp', 'aeo'],
    downloads: ['agentic-commerce-stack-poster'],
  },
  {
    slug: 'llms-txt',
    term: 'llms.txt',
    cluster: 'aeo',
    aliases: ['llms-full.txt'],
    shortAnswer:
      'A site-root file (analogue to robots.txt) that gives AI agents a canonical content map: the most important URLs, what each is about, and the curated long-form context the brand wants quoted.',
    example: 'After publishing /llms.txt, ChatGPT started citing our State-of-UGC page within two weeks instead of pulling from third-party copies.',
    relatedSlugs: ['answer-engine-optimisation-2026-playbook'],
    relatedTerms: ['aeo', 'citation-graph'],
    sameAs: 'https://llmstxt.org/',
  },
  {
    slug: 'answer-engine',
    relatedSlugs: ['answer-engine-optimisation-2026-playbook', 'answer-engine-optimisation-hub', 'make-product-content-readable-by-ai-shopping-agents'],
    term: 'Answer engine',
    cluster: 'aeo',
    aliases: ['generative search', 'LLM search'],
    shortAnswer:
      'A search surface that composes one answer instead of returning a ranked list of links. ChatGPT, Claude, Perplexity, Gemini and Google\'s AI Overview are all answer engines.',
    notTheSameAs: [
      { term: 'Traditional search engine', reason: 'A traditional engine returns links; an answer engine returns one composed answer that may cite multiple sources.' },
    ],
    relatedTerms: ['aeo', 'ai-shopper', 'citation-graph'],
  },
  {
    slug: 'ai-shopper',
    term: 'AI shopper',
    cluster: 'aeo',
    aliases: ['shopping agent', 'AI shopping assistant'],
    shortAnswer:
      'An LLM-powered agent that narrows products to a shortlist for a human or completes the purchase with delegated payment. Two shapes: vertical (inside one retailer\'s store) and horizontal (across retailers).',
    relatedSlugs: ['agentic-ai-shopping-merchant-primer'],
    relatedTerms: ['agentic-commerce', 'answer-engine', 'product-feed-for-agents'],
  },
  {
    slug: 'shopping-agent',
    relatedSlugs: ['agentic-ai-shopping-merchant-primer', 'how-ai-agents-actually-choose-products-12-conversation-teardowns', 'what-is-agentic-commerce'],
    term: 'Shopping agent',
    cluster: 'aeo',
    shortAnswer:
      'Any AI agent operating in the buying flow. Two shapes worth distinguishing: vertical agents embedded inside one retailer\'s site, and horizontal agents (ChatGPT Shopping, Perplexity, retailer-app agents) that span retailers.',
    relatedTerms: ['ai-shopper', 'agentic-commerce'],
  },
  {
    slug: 'citation-graph',
    term: 'Citation graph',
    cluster: 'aeo',
    aliases: ['citation network', 'AI citation map'],
    shortAnswer:
      'The web of cross-references AI engines build between pages on the open web. A page that is well-referenced from authoritative cluster pages gets cited disproportionately more.',
    relatedSlugs: ['answer-engine-optimisation-2026-playbook'],
    relatedTerms: ['citation-footprint', 'aeo'],
  },
  {
    slug: 'citation-footprint',
    relatedSlugs: ['citation-gap-1200-brands-chatgpt-90-days', 'which-ai-engine-cites-you-1200-prompt-study', 'the-42-signal-aeo-scorecard'],
    term: 'Citation footprint',
    cluster: 'aeo',
    shortAnswer:
      'How often and where a brand is quoted by AI engines. Measured by direct probing (asking each engine the brand\'s priority queries) and by referrer logs from the engines\' citation links.',
    relatedTerms: ['citation-graph', 'aeo'],
  },
  {
    slug: 'snippet-quote-frame',
    term: 'Snippet quote-frame',
    cluster: 'aeo',
    aliases: ['quote window', '700-token frame'],
    shortAnswer:
      'The 30-700 token window an AI engine extracts from a page to compose its answer. Pages structured so that a coherent answer fits inside one such window get cited many times more than pages that need stitching.',
    relatedSlugs: ['the-seven-second-window-pdp-as-snippet'],
    relatedTerms: ['pdp-as-snippet', 'aeo'],
  },
  {
    slug: 'structured-data',
    relatedSlugs: ['structured-data-schema-for-product-ugc', 'schema-org-ecommerce-agentic-era-14-schemas-ranked', 'make-product-content-readable-by-ai-shopping-agents'],
    term: 'Structured data',
    cluster: 'aeo',
    shortAnswer:
      'Machine-readable metadata an agent can parse without language inference. Schema.org markup embedded as JSON-LD is the dominant form on the web.',
    relatedTerms: ['schema-org', 'json-ld', 'faqpage-schema'],
    sameAs: 'https://developers.google.com/search/docs/appearance/structured-data',
  },
  {
    slug: 'schema-org',
    relatedSlugs: ['schema-org-ecommerce-agentic-era-14-schemas-ranked', 'twelve-json-ld-shapes-agents-quote', 'structured-data-schema-for-product-ugc'],
    term: 'Schema.org',
    cluster: 'aeo',
    shortAnswer:
      'The shared vocabulary that structured-data markup uses. Co-sponsored by Google, Bing, Yahoo and Yandex. Defines types (Product, Article, FAQPage, DefinedTerm) and their fields.',
    relatedTerms: ['structured-data', 'json-ld'],
    sameAs: 'https://schema.org/',
  },
  {
    slug: 'json-ld',
    relatedSlugs: ['twelve-json-ld-shapes-agents-quote', 'schema-org-ecommerce-agentic-era-14-schemas-ranked', 'structured-data-schema-for-product-ugc'],
    term: 'JSON-LD',
    cluster: 'aeo',
    aliases: ['linked data JSON'],
    shortAnswer:
      'The JSON dialect of schema.org markup, embedded in `<script type="application/ld+json">` tags. The format Google explicitly recommends over Microdata or RDFa.',
    relatedTerms: ['schema-org', 'structured-data'],
    sameAs: 'https://json-ld.org/',
  },
  {
    slug: 'faqpage-schema',
    relatedSlugs: ['faq-page-to-chat-thread-migration', 'twelve-json-ld-shapes-agents-quote', 'how-to-appear-in-google-ai-overviews-sge'],
    term: 'FAQPage schema',
    cluster: 'aeo',
    shortAnswer:
      'The schema.org shape that wraps a list of question/answer pairs. The format that wins "what is X" featured snippets and is most-often quoted by AI engines as the canonical answer to a question.',
    relatedTerms: ['structured-data', 'json-ld', 'speakable-schema'],
  },
  {
    slug: 'product-schema',
    relatedSlugs: ['aggregate-rating-schema-and-rich-snippets', 'schema-org-ecommerce-agentic-era-14-schemas-ranked', 'structured-data-schema-for-product-ugc'],
    term: 'Product schema',
    cluster: 'aeo',
    shortAnswer:
      'The schema.org Product type, carrying price, availability, GTIN, brand, and most importantly AggregateRating and Review. Required for inclusion in agent shortlists and for rich-result eligibility.',
    relatedTerms: ['structured-data', 'aggregate-rating', 'verified-buyer'],
    sameAs: 'https://schema.org/Product',
  },
  {
    slug: 'aggregate-rating',
    relatedSlugs: ['aggregate-rating-schema-and-rich-snippets', 'aeo-for-reviews-getting-ratings-quoted-by-agents', 'reviews-evidence-ai-engines-verified-14x'],
    term: 'AggregateRating schema',
    cluster: 'aeo',
    shortAnswer:
      'The schema.org field carrying a product\'s star rating and total review count, embedded inside Product or Article markup. The single most-impactful AEO signal on a PDP after Product type itself.',
    relatedTerms: ['product-schema', 'verified-buyer'],
  },
  {
    slug: 'speakable-schema',
    relatedSlugs: ['how-to-appear-in-google-ai-overviews-sge', 'voice-commerce-back-qa-schema', 'faq-page-to-chat-thread-migration'],
    term: 'Speakable schema',
    cluster: 'aeo',
    shortAnswer:
      'The schema.org Speakable specification marks parts of a page that voice assistants and AI engines can quote cleanly aloud. Apply it to TL;DR cards, top-of-article answer paragraphs and glossary shortAnswers.',
    relatedTerms: ['structured-data', 'json-ld', 'faqpage-schema'],
    sameAs: 'https://schema.org/SpeakableSpecification',
  },
  {
    slug: 'mcp',
    term: 'MCP (Model Context Protocol)',
    cluster: 'aeo',
    pronunciation: 'M-C-P',
    aliases: ['Model Context Protocol'],
    shortAnswer:
      'Anthropic\'s open standard for exposing tools (catalogue search, cart, checkout) that an AI agent can invoke. The transport layer of agentic commerce.',
    relatedSlugs: ['mcp-tool-calls-product-feeds-the-merchants-guide'],
    relatedTerms: ['merchant-mcp', 'ai-tool-calling', 'agentic-commerce'],
    sameAs: 'https://modelcontextprotocol.io/',
  },
  {
    slug: 'merchant-mcp',
    term: 'Merchant MCP',
    cluster: 'aeo',
    shortAnswer:
      'A merchant-operated MCP server exposing catalogue, cart, inventory and checkout tools that shopping agents can call. The minimum viable interface for a brand to participate in agentic commerce.',
    relatedSlugs: ['mcp-tool-calls-product-feeds-the-merchants-guide'],
    relatedTerms: ['mcp', 'product-feed-for-agents', 'ai-tool-calling'],
  },
  {
    slug: 'ai-tool-calling',
    relatedSlugs: ['mcp-tool-calls-product-feeds-the-merchants-guide', 'the-agentic-commerce-stack-reference-architecture', 'make-product-content-readable-by-ai-shopping-agents'],
    term: 'AI tool calling',
    cluster: 'aeo',
    aliases: ['function calling', 'tool use'],
    shortAnswer:
      'The mechanism by which an AI agent invokes a structured tool exposed via MCP or a similar protocol. Tools return JSON the model uses to compose its next action.',
    relatedTerms: ['mcp', 'merchant-mcp', 'agentic-commerce'],
  },
  {
    slug: 'product-feed-for-agents',
    relatedSlugs: ['mcp-tool-calls-product-feeds-the-merchants-guide', 'syndicate-ugc-to-google-shopping-meta-catalog-ads', 'make-product-content-readable-by-ai-shopping-agents'],
    term: 'Product feed for agents',
    cluster: 'aeo',
    aliases: ['agent-readable feed', 'AI product feed'],
    shortAnswer:
      'The catalogue exposed in agent-readable form, typically JSON over MCP, but also as a feed with rich schema.org markup. Without one, an agent cannot put the brand\'s products into its shortlist.',
    relatedTerms: ['mcp', 'merchant-mcp', 'product-schema'],
  },
  {
    slug: 'pdp-as-snippet',
    term: 'PDP-as-snippet',
    cluster: 'aeo',
    shortAnswer:
      'The design discipline of structuring a product detail page so its first 700 tokens stand as a complete answer when quoted by an AI engine. Hero sentence, use-cases, comparison paragraph, structured Q&A.',
    relatedSlugs: ['the-seven-second-window-pdp-as-snippet'],
    relatedTerms: ['snippet-quote-frame', 'aeo'],
    downloads: ['pdp-rewrite-worksheet'],
  },
  {
    slug: 'seven-second-window',
    term: '7-second window',
    cluster: 'aeo',
    aliases: ['attention window'],
    shortAnswer:
      'The shopper\'s pre-scroll attention budget on a PDP, measured at roughly seven seconds before a scroll-or-bounce decision. Pages that surface the buying answer in the first seven seconds dominate conversion AND citation.',
    relatedSlugs: ['the-seven-second-window-pdp-as-snippet'],
    relatedTerms: ['pdp-as-snippet'],
  },

  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 4, LEGAL + COMPLIANCE
  // ═══════════════════════════════════════════════════════════════
  {
    slug: 'gdpr',
    term: 'GDPR (UGC compliance)',
    cluster: 'legal',
    aliases: ['General Data Protection Regulation', 'EU GDPR'],
    shortAnswer:
      'The EU regulation governing personal data, including identifiable customer content. Commercial reuse of identifiable UGC requires documented lawful basis (in practice, explicit consent), and withdrawal must be honoured within roughly 30 days end-to-end.',
    relatedSlugs: ['gdpr-ugc-compliance'],
    relatedTerms: ['ccpa-cpra', 'uk-gdpr', 'right-to-be-forgotten', 'consent-record'],
    sameAs: 'https://gdpr-info.eu/',
  },
  {
    slug: 'ftc-endorsement-guidelines',
    term: 'FTC Endorsement Guides',
    cluster: 'legal',
    aliases: ['FTC disclosure rules', '#ad rules'],
    shortAnswer:
      'US Federal Trade Commission rules requiring clear and conspicuous disclosure of any material connection between a brand and an endorser. Brands are liable for the endorser\'s disclosure failures, not just their own.',
    relatedSlugs: ['ftc-endorsement-guidelines'],
    relatedTerms: ['asa-cap-uk', 'gdpr'],
    sameAs: 'https://www.ftc.gov/business-guidance/resources/disclosures-101-social-media-influencers',
  },
  {
    slug: 'ccpa-cpra',
    term: 'CCPA / CPRA',
    cluster: 'legal',
    aliases: ['California Consumer Privacy Act', 'California Privacy Rights Act'],
    shortAnswer:
      'California\'s consumer privacy regime, requiring disclosure of personal-data collection, opt-out of "sale", and deletion-on-request within 45 days. Applies to most ecommerce brands serving California residents.',
    relatedSlugs: ['ccpa-customer-reviews'],
    relatedTerms: ['gdpr', 'right-to-be-forgotten'],
    sameAs: 'https://oag.ca.gov/privacy/ccpa',
  },
  {
    slug: 'uk-gdpr',
    relatedSlugs: ['gdpr-ugc-compliance', 'gdpr-consent-and-ugc', 'how-to-collect-ugc-legally-instagram-tiktok'],
    term: 'UK GDPR',
    cluster: 'legal',
    shortAnswer:
      'The UK\'s carbon copy of EU GDPR, in force since Brexit. Substantively identical for UGC purposes; the ICO enforces. Recordkeeping, consent and revocation requirements match EU GDPR.',
    relatedTerms: ['gdpr', 'consent-record'],
  },
  {
    slug: 'right-to-be-forgotten',
    relatedSlugs: ['when-a-creator-deletes-the-original-post', 'gdpr-ugc-compliance', 'rights-workflow-2026-merchants-wish-they-built'],
    term: 'Right to be forgotten (RTBF)',
    cluster: 'legal',
    aliases: ['right to erasure', 'GDPR Article 17'],
    shortAnswer:
      'A creator\'s GDPR right to revoke consent and have their content taken down across every surface (PDP, email, paid ad cache, retail signage) within the 30-day SLA. The highest-risk failure mode in UGC operations at scale.',
    relatedTerms: ['gdpr', 'consent-record', 'ugc-rights-management'],
  },
  {
    slug: 'consent-record',
    relatedSlugs: ['how-to-get-ugc-rights', 'rights-workflow-2026-merchants-wish-they-built', 'gdpr-consent-and-ugc'],
    term: 'Consent record',
    cluster: 'legal',
    aliases: ['rights record', 'permission log'],
    shortAnswer:
      'The audit-trail artefact of a creator\'s yes. A valid record names the asset, the scope of use, the duration, the creator\'s unambiguous yes, and the revocation endpoint.',
    relatedTerms: ['ugc-rights-management', 'affirmative-consent', 'gdpr'],
  },
  {
    slug: 'dsar',
    relatedSlugs: ['gdpr-ugc-compliance', 'gdpr-consent-and-ugc', 'ccpa-customer-reviews'],
    term: 'Data subject access request (DSAR)',
    cluster: 'legal',
    pronunciation: 'D-S-A-R',
    shortAnswer:
      'A consumer\'s request to see all personal data a controller holds about them. Must be served within 30 days under GDPR. UGC programmes must be able to surface every consented asset for a named creator.',
    relatedTerms: ['gdpr', 'consent-record'],
  },
  {
    slug: 'asa-cap-uk',
    relatedSlugs: ['ftc-endorsement-guidelines', 'how-to-collect-ugc-legally-instagram-tiktok', 'gdpr-ugc-compliance'],
    term: 'ASA / CAP (UK)',
    cluster: 'legal',
    pronunciation: 'A-S-A',
    shortAnswer:
      'The UK Advertising Standards Authority and Committee of Advertising Practice. Sets and enforces disclosure rules on social media advertising, broadly equivalent to FTC Endorsement Guides.',
    relatedTerms: ['ftc-endorsement-guidelines'],
    sameAs: 'https://www.asa.org.uk/',
  },
  {
    slug: 'affirmative-consent',
    relatedSlugs: ['gdpr-consent-and-ugc', 'how-to-collect-ugc-legally-instagram-tiktok', 'how-to-get-ugc-rights'],
    term: 'Affirmative consent',
    cluster: 'legal',
    aliases: ['active opt-in', 'explicit consent'],
    shortAnswer:
      'An active "yes" from the data subject (typed, ticked, tapped), as opposed to passive opt-in or silence. The GDPR standard for commercial reuse of identifiable content.',
    relatedTerms: ['gdpr', 'consent-record', 'auto-dm-consent-flow'],
  },
  {
    slug: 'image-rights',
    relatedSlugs: ['how-to-get-ugc-rights', 'ugc-rights-and-permissions-guide', 'rights-clearance-unsexy-moat'],
    term: 'Image rights / personality rights',
    cluster: 'legal',
    aliases: ['personality rights', 'right of publicity'],
    shortAnswer:
      'The right of an identifiable person to control commercial reuse of their likeness, distinct from copyright. Especially relevant when a UGC photo features people other than the creator.',
    relatedTerms: ['gdpr', 'ugc-rights-management'],
  },
  {
    slug: 'music-licensing-in-ugc',
    relatedSlugs: ['music-licensing-in-ugc-video', 'copyright-fair-use-ugc', 'ugc-rights-and-permissions-guide'],
    term: 'Music licensing in UGC',
    cluster: 'legal',
    shortAnswer:
      'The catch: TikTok or Instagram licence music for use on their platforms; that licence does not transfer when you rehost the clip on your PDP. Strip or replace the audio, or licence the track separately.',
    relatedTerms: ['brand-safety-filter', 'ugc-rights-management'],
  },
  {
    slug: 'review-gating',
    relatedSlugs: ['how-to-get-more-google-reviews', 'local-service-review-examples', 'ftc-endorsement-guidelines'],
    term: 'Review gating',
    cluster: 'legal',
    aliases: ['selective review solicitation', 'sentiment pre-screening'],
    shortAnswer:
      'Review gating is filtering which customers get asked to leave a public review based on how happy they are (a private pre-screen routes satisfied customers to Google or Trustpilot and unhappy ones to a private feedback form instead). It is explicitly prohibited by the FTC and by Google and Trustpilot policy when it selectively suppresses negative reviews from ever being written publicly.',
    longBody:
      'The line is not "asking for reviews", it is "asking selectively". Inviting every customer to leave a review, using the same process regardless of how the visit or order went, is the legitimate, policy-compliant practice: some of those customers will leave negative reviews, and that is the point, an honest review base. Gating happens when a sentiment question comes first ("were you happy with your visit? Yes / No") and only the "yes" answers get routed to a public review link, while "no" answers are quietly diverted to a private inbox. The FTC\'s 2024 Rule on Consumer Reviews and Testimonials formally bars this: a business cannot misrepresent that the reviews on display represent a fair sample when it has been filtering out the negative ones before they are written. Google\'s and Trustpilot\'s own guidelines for businesses independently prohibit the same behaviour and will remove reviews, downrank listings or suspend profiles over it.',
    notTheSameAs: [
      { term: 'Universal review invitations', reason: 'Asking every customer, regardless of expected sentiment, through the same channel and process, is the compliant baseline; nothing here restricts asking for reviews itself.' },
      { term: 'Private feedback channels', reason: 'Offering an additional private way to reach support (a "if anything wasn\'t right, reply here instead") is fine on its own; it becomes gating only when it replaces the public review invitation for customers pre-screened as unhappy.' },
    ],
    example: 'A restaurant that texts every table the same review-request link, win or lose, is asking cleanly. A restaurant that only sends that link to tables who first answered "yes, everything was great" on a private survey is gating.',
    relatedTerms: ['ftc-endorsement-guidelines', 'asa-cap-uk', 'affirmative-consent'],
    sameAs: 'https://www.ftc.gov/business-guidance/resources/consumer-reviews-testimonials-rule-questions-answers',
  },

  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 5, METRICS
  // ═══════════════════════════════════════════════════════════════
  {
    slug: 'aov',
    relatedSlugs: ['how-to-measure-ugc-roi', 'ugc-roi-benchmark-report', 'ugc-conversion-rate-boost'],
    term: 'AOV (average order value)',
    cluster: 'metrics',
    pronunciation: 'A-O-V',
    shortAnswer:
      'The mean basket value across all orders in a window. The easiest CFO-friendly metric for UGC impact because no causal-attribution argument is required: the same shopper buys a larger basket.',
    relatedTerms: ['rpv', 'conversion-rate', 'lift'],
  },
  {
    slug: 'core-web-vitals',
    term: 'CWV (Core Web Vitals)',
    cluster: 'metrics',
    pronunciation: 'C-W-V',
    aliases: ['Core Web Vitals'],
    shortAnswer:
      'Google\'s three field-measured metrics: Largest Contentful Paint (<2.5s), Cumulative Layout Shift (<0.1) and Interaction to Next Paint (<200ms). Confirmed ranking factor since 2021 Page Experience update.',
    relatedSlugs: ['core-web-vitals-ugc-widgets'],
    relatedTerms: ['lcp', 'cls', 'inp'],
    sameAs: 'https://web.dev/articles/vitals',
  },
  {
    slug: 'roas',
    relatedSlugs: ['sourcing-ugc-for-paid-ads', 'how-to-measure-ugc-roi', 'syndicate-ugc-to-google-shopping-meta-catalog-ads'],
    term: 'ROAS (return on ad spend)',
    cluster: 'metrics',
    pronunciation: 'ROW-as',
    shortAnswer:
      'Revenue attributed to ad spend divided by the ad spend itself. The headline paid-social metric. UGC creative averages 1.5-2x ROAS over brand-produced creative on the same campaign and spend.',
    relatedTerms: ['aov', 'cpa', 'cpm'],
  },
  {
    slug: 'pdp',
    relatedSlugs: ['anatomy-conversational-pdp-nine-components', 'pdp-before-after-what-ugc-changes', 'why-product-pages-dont-convert'],
    term: 'PDP (product detail page)',
    cluster: 'metrics',
    pronunciation: 'P-D-P',
    aliases: ['product detail page', 'product page'],
    shortAnswer:
      'The page rendering a single product. The single highest-leverage UGC surface (median 18% conversion lift) and the dominant entry point for shoppers arriving from social, search, and AI agents.',
    relatedTerms: ['pdp-as-snippet', 'conversion-rate'],
  },
  {
    slug: 'conversion-rate',
    relatedSlugs: ['ugc-conversion-rate-boost', 'how-to-measure-ugc-roi', 'reducing-cart-abandonment-with-social-proof'],
    term: 'CR / Conversion rate',
    cluster: 'metrics',
    pronunciation: 'C-R',
    aliases: ['conversion rate'],
    shortAnswer:
      'Sessions that convert to a purchase divided by all sessions. The headline ecommerce metric. UGC adds a median +18% to PDP CR in our 2,400-brand dataset.',
    relatedTerms: ['rpv', 'lift', 'holdout-test'],
  },
  {
    slug: 'rpv',
    relatedSlugs: ['how-to-measure-ugc-roi', 'ugc-roi-benchmark-report', 'ugc-conversion-rate-boost'],
    term: 'RPV (revenue per visitor)',
    cluster: 'metrics',
    pronunciation: 'R-P-V',
    shortAnswer:
      'Total revenue divided by total sessions. The compound metric (conversion times AOV) that the CFO actually cares about. UGC programmes lift RPV by a median 44% on the page-level cohort.',
    relatedTerms: ['conversion-rate', 'aov'],
  },
  {
    slug: 'ctr',
    relatedSlugs: ['sourcing-ugc-for-paid-ads', 'ugc-in-email-and-klaviyo-flows', 'how-to-measure-ugc-roi'],
    term: 'CTR (click-through rate)',
    cluster: 'metrics',
    pronunciation: 'C-T-R',
    shortAnswer:
      'Clicks divided by impressions. The headline performance metric for ads, lifecycle email and on-site widgets. UGC creative lifts paid-social CTR by roughly 28% over brand-produced creative.',
    relatedTerms: ['roas', 'cpm', 'cpa'],
  },
  {
    slug: 'cpm',
    relatedSlugs: ['sourcing-ugc-for-paid-ads', 'the-economics-of-ugc-2026-per-impression-billing', 'how-to-measure-ugc-roi'],
    term: 'CPM',
    cluster: 'metrics',
    pronunciation: 'C-P-M',
    aliases: ['cost per mille', 'cost per thousand impressions'],
    shortAnswer:
      'Cost per 1,000 impressions. The pricing unit for paid social and display ads. UGC creative reduces CPM by 15-20% because the platform algorithm rewards native-feeling content.',
    relatedTerms: ['roas', 'cpa', 'ctr'],
  },
  {
    slug: 'cpa',
    relatedSlugs: ['sourcing-ugc-for-paid-ads', 'how-to-measure-ugc-roi', 'combining-affiliate-and-ugc'],
    term: 'CPA (cost per acquisition)',
    cluster: 'metrics',
    pronunciation: 'C-P-A',
    shortAnswer:
      'Total spend divided by acquired customers. The bottom-line paid metric. UGC programmes routinely drop CPA by 22-34% on cold audiences because the creative converts at higher CTR for lower CPM.',
    relatedTerms: ['roas', 'cpm', 'ctr'],
  },
  {
    slug: 'engager-vs-pagelevel',
    term: 'Engager-only vs page-level',
    cluster: 'metrics',
    aliases: ['UGC denominator'],
    shortAnswer:
      'The two cohorts that change every UGC statistic. Page-level = all PDP visitors (used for revenue forecasting). Engager-only = shoppers who actively interacted with UGC (used for placement decisions).',
    example: 'The 144% Bazaarvoice number is engager-only; our 18% median is page-level. Both are real; use the right one for the question.',
    relatedSlugs: ['state-of-ugc-2026-report'],
    relatedTerms: ['conversion-rate', 'holdout-test'],
  },
  {
    slug: 'holdout-test',
    term: 'Holdout test',
    cluster: 'metrics',
    aliases: ['A/B holdout', 'control arm'],
    shortAnswer:
      'The only way to measure UGC lift defensibly. Serve UGC to half of PDP visitors, hide it from the other half, measure the delta. Anyone reporting UGC lift without a holdout has assumed it, not measured it.',
    relatedSlugs: ['how-to-measure-ugc-roi'],
    relatedTerms: ['lift', 'conversion-rate'],
  },
  {
    slug: 'lift',
    relatedSlugs: ['ugc-incrementality-testing-holdout', 'how-to-measure-ugc-roi', 'ab-testing-ugc-placement'],
    term: 'Lift / incremental lift',
    cluster: 'metrics',
    aliases: ['incremental lift', 'incremental conversion'],
    shortAnswer:
      'The conversion delta between the test arm and the control arm in a holdout test. The only honest measure of UGC impact; everything else is a vanity number.',
    relatedTerms: ['holdout-test', 'conversion-rate'],
  },
  {
    slug: 'time-to-first-cart-click',
    term: 'Time-to-first-cart-click',
    cluster: 'metrics',
    shortAnswer:
      'Seconds from PDP land to the first add-to-cart tap. Shoppable video drops this from a median 38 seconds (static gallery) to 11 seconds.',
    relatedSlugs: ['shoppable-video-vs-photography'],
    relatedTerms: ['shoppable-video', 'pdp'],
  },
  {
    slug: 'watch-time',
    relatedSlugs: ['ideal-length-for-a-shoppable-video', 'mobile-first-shoppable-video-design', 'what-is-shoppable-video'],
    term: 'Watch time',
    cluster: 'metrics',
    shortAnswer:
      'Seconds of video played per video impression. A working shoppable-video implementation hits 60%+ of length; below 40% the video is wrong for the audience (length, pacing, or content).',
    relatedTerms: ['shoppable-video', 'dwell-time'],
  },
  {
    slug: 'dwell-time',
    relatedSlugs: ['core-web-vitals-ugc-widgets', 'why-product-pages-dont-convert', 'ugc-conversion-rate-boost'],
    term: 'Dwell time',
    cluster: 'metrics',
    aliases: ['time on page', 'session duration'],
    shortAnswer:
      'Seconds spent on a page per session. UGC lifts PDP dwell time by a median +31% in our dataset, which correlates with downstream organic search ranking via Google\'s user-signal model.',
    relatedTerms: ['watch-time', 'conversion-rate'],
  },
  {
    slug: 'yes-rate',
    term: 'Yes-rate (rights)',
    cluster: 'metrics',
    aliases: ['consent rate', 'approval rate'],
    shortAnswer:
      'Pieces with consent secured divided by pieces requested. Median sits around 38% on a competent UGC programme; top quartile hits 55-60% with automated workflows and one-tap consent forms.',
    relatedSlugs: ['how-to-get-ugc-rights'],
    relatedTerms: ['ugc-rights-management', 'auto-dm-consent-flow'],
  },
  {
    slug: 'lcp',
    term: 'LCP (Largest Contentful Paint)',
    cluster: 'metrics',
    pronunciation: 'L-C-P',
    shortAnswer:
      'The time to the largest visible element rendering. Google\'s Good threshold is 2.5 seconds on mobile. A poorly-built UGC widget can push LCP past 4 seconds, killing both SEO and conversion.',
    relatedSlugs: ['core-web-vitals-ugc-widgets'],
    relatedTerms: ['core-web-vitals', 'cls', 'inp'],
  },
  {
    slug: 'cls',
    relatedSlugs: ['core-web-vitals-ugc-widgets', 'social-proof-without-slowing-your-store', 'accessibility-in-shoppable-video'],
    term: 'CLS (Cumulative Layout Shift)',
    cluster: 'metrics',
    pronunciation: 'C-L-S',
    shortAnswer:
      'The score for unintended layout shifts after initial render. Google\'s Good threshold is 0.1. Widgets that inject gallery content without reserving the right vertical space tank CLS catastrophically.',
    relatedTerms: ['core-web-vitals', 'lcp', 'inp'],
  },
  {
    slug: 'inp',
    relatedSlugs: ['core-web-vitals-ugc-widgets', 'social-proof-without-slowing-your-store', 'mobile-first-shoppable-video-design'],
    term: 'INP (Interaction to Next Paint)',
    cluster: 'metrics',
    pronunciation: 'I-N-P',
    shortAnswer:
      'The latency between any user interaction and the next visual update. Replaced FID as a Core Web Vital in March 2024. Good threshold is 200ms.',
    relatedTerms: ['core-web-vitals', 'lcp', 'cls'],
    sameAs: 'https://web.dev/articles/inp',
  },
  {
    slug: 'bounce-rate',
    relatedSlugs: ['core-web-vitals-ugc-widgets', 'social-proof-without-slowing-your-store', 'why-product-pages-dont-convert'],
    term: 'Bounce rate',
    cluster: 'metrics',
    shortAnswer:
      'Single-page sessions divided by all sessions. Brands meeting all three Core Web Vitals thresholds see 24% lower bounce rates than brands missing any of them.',
    relatedTerms: ['core-web-vitals', 'conversion-rate'],
  },

  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 6, PLATFORMS
  // ═══════════════════════════════════════════════════════════════
  {
    slug: 'social-commerce',
    term: 'Social commerce',
    cluster: 'platforms',
    shortAnswer:
      'Commerce that is triggered or completed inside a social platform (Instagram Shopping, TikTok Shop, Pinterest Buy). Global market $2.6T in 2025 (eMarketer), projected $6.2T by 2030.',
    relatedSlugs: ['100-social-commerce-statistics-2026'],
    relatedTerms: ['tiktok-shop', 'instagram-graph-api'],
    sameAs: 'https://en.wikipedia.org/wiki/Social_commerce',
  },
  {
    slug: 'shopify-app-block',
    relatedSlugs: ['embed-shoppable-video-shopify', 'best-ugc-platform-for-shopify-buyers-guide', 'make-instagram-reels-tiktoks-shoppable-shopify'],
    term: 'Shopify App Block',
    cluster: 'platforms',
    aliases: ['theme app extension', 'Online Store 2.0 block'],
    shortAnswer:
      'Shopify\'s embed format for third-party widgets in Online Store 2.0 themes. The format Idukki ships through. Lets merchants drop widgets in via the theme editor without code.',
    industries: ['fashion-and-apparels', 'beauty-and-cosmetics'],
    relatedTerms: ['bigcommerce', 'woocommerce', 'wix'],
  },
  {
    slug: 'bigcommerce',
    relatedSlugs: ['add-shoppable-ugc-to-bigcommerce', 'open-letter-to-shopify-bigcommerce-woocommerce-2027', 'ugc-platform-guide'],
    term: 'BigCommerce',
    cluster: 'platforms',
    shortAnswer:
      'SaaS ecommerce platform Idukki integrates with. Stencil theme architecture, B2B and headless-friendly. Native script-tag and theme-block injection paths.',
    relatedTerms: ['shopify-app-block', 'woocommerce', 'magento-adobe-commerce'],
  },
  {
    slug: 'woocommerce',
    relatedSlugs: ['add-ugc-widgets-to-woocommerce', 'ugc-shoppable-video-magento-adobe-commerce', 'best-ugc-platform-for-shopify-buyers-guide'],
    term: 'WooCommerce',
    cluster: 'platforms',
    shortAnswer:
      'The WordPress ecommerce plugin. Largest ecommerce platform by raw deployment count. Idukki integrates via a WP plugin plus Gutenberg block.',
    relatedTerms: ['shopify-app-block', 'bigcommerce'],
  },
  {
    slug: 'magento-adobe-commerce',
    relatedSlugs: ['ugc-shoppable-video-magento-adobe-commerce', 'add-ugc-widgets-to-woocommerce', 'add-shoppable-ugc-to-bigcommerce'],
    term: 'Magento / Adobe Commerce',
    cluster: 'platforms',
    shortAnswer:
      'Adobe\'s enterprise ecommerce platform (formerly Magento). Highly customisable, heavier engineering than Shopify or Wix. Idukki supports both Magento Open Source and Adobe Commerce.',
    relatedTerms: ['shopify-app-block', 'bigcommerce'],
  },
  {
    slug: 'wix',
    relatedSlugs: ['add-ugc-custom-storefront-rest-api', 'best-ugc-platform-for-shopify-buyers-guide', 'add-shoppable-ugc-to-bigcommerce'],
    term: 'Wix',
    cluster: 'platforms',
    shortAnswer:
      'All-in-one website builder with native ecommerce. Idukki integrates via Wix Velo. Lower customisation ceiling than Shopify but faster to launch.',
    relatedTerms: ['shopify-app-block'],
  },
  {
    slug: 'headless-commerce',
    relatedSlugs: ['add-ugc-custom-storefront-rest-api', 'the-agentic-commerce-stack-reference-architecture', 'core-web-vitals-ugc-widgets'],
    term: 'Headless commerce',
    cluster: 'platforms',
    shortAnswer:
      'Architecture where the storefront (Next.js, Astro, etc.) is decoupled from the commerce backend (Shopify, BigCommerce, commercetools). UGC widgets ship via JS embed rather than theme-block.',
    relatedTerms: ['shopify-app-block', 'bigcommerce'],
  },
  {
    slug: 'klaviyo',
    relatedSlugs: ['ugc-in-email-and-klaviyo-flows', 'ugc-across-klaviyo-gorgias-recharge', 'syndicate-ugc-to-google-shopping-meta-catalog-ads'],
    term: 'Klaviyo',
    cluster: 'platforms',
    shortAnswer:
      'Email and SMS platform Idukki\'s lifecycle block plugs into. Dynamic UGC blocks per segment, refreshed at send-time. Submission rate jumps 2.4x with AMP-for-Email inline forms.',
    relatedTerms: ['post-purchase-prompt'],
  },
  {
    slug: 'trustpilot',
    relatedSlugs: ['trustpilot-vs-feefo-vs-google-reviews', 'best-review-platforms-media', 'aeo-for-reviews-getting-ratings-quoted-by-agents'],
    term: 'Trustpilot',
    cluster: 'platforms',
    shortAnswer:
      'Third-party review platform Idukki ingests from. Aggregated reviews surface on-site as widgets with combined-score header plus per-source breakdown.',
    relatedTerms: ['google-reviews', 'feefo'],
    sameAs: 'https://www.trustpilot.com/',
  },
  {
    slug: 'google-reviews',
    relatedSlugs: ['google-reviews-as-shoppable-content', 'trustpilot-vs-feefo-vs-google-reviews', 'best-review-platforms-media'],
    term: 'Google Reviews',
    cluster: 'platforms',
    shortAnswer:
      'Google\'s review surface, ingested via the Business Profile API. The single most-cited review source for "what do customers say" queries in AI engines.',
    relatedTerms: ['trustpilot', 'feefo', 'aggregate-rating'],
  },
  {
    slug: 'feefo',
    relatedSlugs: ['trustpilot-vs-feefo-vs-google-reviews', 'best-review-platforms-media', 'reviews-evidence-ai-engines-verified-14x'],
    term: 'Feefo',
    cluster: 'platforms',
    shortAnswer:
      'UK-origin review platform Idukki integrates with. Verified-buyer-only by default, which lifts AI citation weighting compared to mixed-source review widgets.',
    relatedTerms: ['trustpilot', 'google-reviews', 'verified-buyer'],
  },
  {
    slug: 'instagram-graph-api',
    relatedSlugs: ['how-to-collect-ugc-legally-instagram-tiktok', 'turn-instagram-into-a-sales-channel-with-ugc', 'make-instagram-reels-tiktoks-shoppable-shopify'],
    term: 'Instagram Graph API',
    cluster: 'platforms',
    aliases: ['IG Graph API', 'IG API'],
    shortAnswer:
      'Meta\'s authorised API for inbound Instagram content. The only sanctioned route to pull tagged posts, hashtag mentions and creator profile data at programme scale.',
    relatedTerms: ['inbound-capture', 'reel'],
  },
  {
    slug: 'tiktok-shop',
    term: 'TikTok Shop',
    cluster: 'platforms',
    shortAnswer:
      'TikTok\'s native commerce surface. $15.82B US sales in 2025 (+108% YoY); $64.3B global GMV. Captures roughly 18% of US social commerce.',
    relatedSlugs: ['100-social-commerce-statistics-2026'],
    relatedTerms: ['social-commerce'],
  },
  {
    slug: 'meta-pixel-capi',
    relatedSlugs: ['syndicate-ugc-to-google-shopping-meta-catalog-ads', 'sourcing-ugc-for-paid-ads', 'how-to-measure-ugc-roi'],
    term: 'Meta Pixel / CAPI',
    cluster: 'platforms',
    aliases: ['Meta Conversions API', 'Meta CAPI'],
    shortAnswer:
      'Meta\'s client + server-side event ingestion. Idukki widgets fire impression / engagement / hotspot-click / ATC events to both channels so attribution survives Apple privacy changes.',
    relatedTerms: ['roas', 'instagram-graph-api'],
  },

  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 7, SOCIAL FORMATS (platform UI)
  // ═══════════════════════════════════════════════════════════════
  {
    slug: 'reel',
    relatedSlugs: ['make-instagram-reels-tiktoks-shoppable-shopify', 'make-product-content-readable-by-ai-shopping-agents', 'instagram-vs-tiktok-ugc'],
    term: 'Reel',
    cluster: 'platform-ui',
    aliases: ['Instagram Reel', 'IG Reel'],
    shortAnswer:
      'Instagram\'s vertical short-form video format (up to 90 seconds). Replaced IGTV; the dominant video format on the platform. Ideal source for UGC pulled into PDP gallery walls.',
    relatedTerms: ['vertical-portrait-video', 'reels-layout', 'tiktok-shop'],
    sameAs: 'https://en.wikipedia.org/wiki/Instagram#Reels',
  },
  {
    slug: 'post',
    relatedSlugs: ['what-is-ugc-and-why-it-converts', 'instagram-vs-tiktok-ugc', 'how-to-get-more-ugc-without-paying'],
    term: 'Post (social post)',
    cluster: 'platform-ui',
    aliases: ['feed post', 'in-feed post'],
    shortAnswer:
      'A static image or short video published to the main feed of a social platform. The standard format for inbound UGC: photo posts hashtagged or tagged @brand are the largest single source of pull.',
    relatedTerms: ['carousel', 'reel', 'story'],
  },
  {
    slug: 'carousel',
    relatedSlugs: ['what-is-ugc-and-why-it-converts', 'instagram-vs-tiktok-ugc', 'shoppable-gallery-guide'],
    term: 'Carousel',
    cluster: 'platform-ui',
    aliases: ['multi-image post', 'gallery post'],
    shortAnswer:
      'A single post with multiple images or video clips swiped through inline. Up to 10 slides on Instagram, similar on Facebook. Pulls 1.4x the engagement of single-image posts on average.',
    relatedTerms: ['post', 'reel', 'story'],
  },
  {
    slug: 'story',
    relatedSlugs: ['instagram-vs-tiktok-ugc', 'turn-instagram-into-a-sales-channel-with-ugc', 'what-is-ugc-and-why-it-converts'],
    term: 'Story',
    cluster: 'platform-ui',
    aliases: ['Instagram Story', 'IG Story', 'ephemeral post'],
    shortAnswer:
      'A 24-hour ephemeral post format on Instagram, Facebook and TikTok. High velocity, low archival value. Re-shared and reposted Story content is a strong inbound UGC source if captured quickly.',
    relatedTerms: ['post', 'reel', 'carousel'],
  },
  {
    slug: 'pin',
    relatedSlugs: ['pinterest-shoppable-content-source', 'instagram-vs-tiktok-ugc', 'what-is-ugc-and-why-it-converts'],
    term: 'Pin (Pinterest)',
    cluster: 'platform-ui',
    aliases: ['Pinterest Pin'],
    shortAnswer:
      'Pinterest\'s native post format: an image or short video tied to a URL, "pinned" to a board by the creator. 89% of weekly Pinterest users use the platform for purchase inspiration (Comscore).',
    relatedTerms: ['post', 'social-commerce'],
  },

  // ═══════════════════════════════════════════════════════════════
  // CLUSTER 8, INDUSTRY-SPECIFIC
  // ═══════════════════════════════════════════════════════════════
  // Fashion
  {
    slug: 'fit-check',
    relatedSlugs: ['best-ugc-platform-for-shopify-buyers-guide', 'best-shoppable-video-fashion', 'fashion-and-apparels-ugc-playbook'],
    term: 'Fit check / fit pic',
    cluster: 'vertical',
    aliases: ['fit pic', 'OOTD'],
    shortAnswer:
      'A customer\'s photo or video specifically focused on showing how an apparel item fits on their body. The single highest-converting UGC format on fashion PDPs.',
    industries: ['fashion-and-apparels'],
    relatedTerms: ['try-on-haul', 'ootd', 'before-after-slider'],
  },
  {
    slug: 'try-on-haul',
    relatedSlugs: ['best-shoppable-video-fashion', 'fashion-and-apparels-ugc-playbook', 'ugc-for-footwear-brands'],
    term: 'Try-on haul',
    cluster: 'vertical',
    shortAnswer:
      'A video format where the creator tries on multiple pieces from a brand or order, showing fit, fabric and styling for each. Dominant format on TikTok for fashion and accessories.',
    industries: ['fashion-and-apparels'],
    relatedTerms: ['fit-check', 'ootd', 'reel'],
  },
  {
    slug: 'ootd',
    relatedSlugs: ['fashion-and-apparels-ugc-playbook', 'best-shoppable-video-fashion', 'ugc-for-athleisure'],
    term: 'OOTD (Outfit of the day)',
    cluster: 'vertical',
    pronunciation: 'O-O-T-D',
    shortAnswer:
      'A daily styled-outfit post format, often tagged #OOTD. Long-running format on Instagram and TikTok; one of the easiest hashtag inbound-capture sources for fashion brands.',
    industries: ['fashion-and-apparels'],
    relatedTerms: ['fit-check', 'branded-hashtag'],
  },
  {
    slug: 'before-after-slider',
    relatedSlugs: ['best-ugc-beauty-skincare', 'beauty-and-cosmetics-ugc-playbook', 'pdp-before-after-what-ugc-changes'],
    term: 'Before/after slider',
    cluster: 'vertical',
    aliases: ['comparison slider'],
    shortAnswer:
      'A UI element showing two photos side-by-side or via a draggable divider. Dominant format for skincare results and apparel try-on outcomes.',
    industries: ['fashion-and-apparels', 'beauty-and-cosmetics'],
    relatedTerms: ['fit-check', 'tutorial'],
  },

  // Beauty
  {
    slug: 'beauty-routine',
    relatedSlugs: ['beauty-and-cosmetics-ugc-playbook', 'best-ugc-beauty-skincare', 'ugc-for-skincare'],
    term: 'Beauty routine',
    cluster: 'vertical',
    aliases: ['skincare routine', 'morning routine'],
    shortAnswer:
      'A creator walking through their day or evening sequence of products, in order. Highest-converting beauty UGC format because it captures multi-product context (cross-sell built in).',
    industries: ['beauty-and-cosmetics'],
    relatedTerms: ['tutorial', 'swatch', 'reel'],
  },
  {
    slug: 'swatch',
    relatedSlugs: ['beauty-and-cosmetics-ugc-playbook', 'best-ugc-beauty-skincare', 'ugc-for-skincare'],
    term: 'Swatch',
    cluster: 'vertical',
    aliases: ['shade swatch', 'colour swatch'],
    shortAnswer:
      'A close-up photo or video of a product applied to skin (foundation, blush, lipstick) showing the actual colour on a real skin tone. Critical for shade-match-driven categories.',
    industries: ['beauty-and-cosmetics'],
    relatedTerms: ['shade-match', 'beauty-routine'],
  },
  {
    slug: 'tutorial',
    relatedSlugs: ['beauty-and-cosmetics-ugc-playbook', 'best-ugc-beauty-skincare', 'best-shoppable-video-fashion'],
    term: 'Tutorial (beauty/skincare)',
    cluster: 'vertical',
    aliases: ['how-to tutorial', 'application tutorial'],
    shortAnswer:
      'A step-by-step video showing how to apply a product (eye look, contour, routine). 89% of consumers say a tutorial-style video convinced them to buy a beauty product (Wyzowl).',
    industries: ['beauty-and-cosmetics'],
    relatedTerms: ['beauty-routine', 'swatch'],
  },
  {
    slug: 'shade-match',
    relatedSlugs: ['beauty-and-cosmetics-ugc-playbook', 'best-ugc-beauty-skincare', 'reducing-returns-with-pre-purchase-ugc'],
    term: 'Shade match',
    cluster: 'vertical',
    shortAnswer:
      'The shopper\'s "will this work on my skin tone" question. Solved on UGC-enabled PDPs by surfacing swatches filtered by skin-tone tag. Lifts beauty conversion +29% on average (Idukki dataset).',
    industries: ['beauty-and-cosmetics'],
    relatedTerms: ['swatch', 'beauty-routine'],
  },

  // Jewellery / accessories
  {
    slug: 'stacking',
    relatedSlugs: ['accessories-and-jewelery-ugc-playbook', 'shop-the-look-from-a-customer-photo', 'fashion-and-apparels-ugc-playbook'],
    term: 'Stacking',
    cluster: 'vertical',
    aliases: ['ring stack', 'necklace layering'],
    shortAnswer:
      'The styling pattern of wearing multiple pieces together (rings on one finger, layered necklaces). Drives cross-product UGC and multi-SKU basket adds on jewellery PDPs.',
    industries: ['accessories-and-jewelery'],
    relatedTerms: ['pairing', 'how-i-wear-it'],
  },
  {
    slug: 'pairing',
    relatedSlugs: ['accessories-and-jewelery-ugc-playbook', 'shop-the-look-from-a-customer-photo', 'fashion-and-apparels-ugc-playbook'],
    term: 'Pairing (accessories)',
    cluster: 'vertical',
    shortAnswer:
      'The visual story of how a specific accessory pairs with a specific outfit, often documented as a daily-wear post. Highest-converting accessories UGC format.',
    industries: ['accessories-and-jewelery', 'fashion-and-apparels'],
    relatedTerms: ['stacking', 'ootd'],
  },
  {
    slug: 'how-i-wear-it',
    relatedSlugs: ['accessories-and-jewelery-ugc-playbook', 'fashion-and-apparels-ugc-playbook', 'shop-the-look-from-a-customer-photo'],
    term: '"How I wear it" content',
    cluster: 'vertical',
    shortAnswer:
      'Customer-shot styling content showing one item worn multiple ways. Dominant inbound UGC format for jewellery, hats, scarves, bags.',
    industries: ['accessories-and-jewelery'],
    relatedTerms: ['pairing', 'stacking', 'ootd'],
  },

  // Home + lifestyle / furniture
  {
    slug: 'in-your-room-photo',
    relatedSlugs: ['furniture-ugc-playbook', 'home-lifestyle-ugc-playbook', 'shoppable-video-furniture'],
    term: '"In your room" photo',
    cluster: 'vertical',
    aliases: ['real-room photo', 'in-situ photo'],
    shortAnswer:
      'A customer-shot photo of furniture or home decor in the buyer\'s actual room, with real lighting and surrounding context. Single highest-converting UGC format for considered-purchase home categories.',
    industries: ['home-lifestyle', 'furniture'],
    relatedTerms: ['styling-photo', 'mood-board', 'size-scale-visualisation'],
  },
  {
    slug: 'styling-photo',
    relatedSlugs: ['furniture-ugc-playbook', 'home-lifestyle-ugc-playbook', 'shop-the-look-from-a-customer-photo'],
    term: 'Styling photo',
    cluster: 'vertical',
    shortAnswer:
      'A composed photo of a home item arranged with complementary pieces in context (vase styled with books on a coffee table). The interior-design adjacent UGC pattern.',
    industries: ['home-lifestyle'],
    relatedTerms: ['in-your-room-photo', 'mood-board'],
  },
  {
    slug: 'mood-board',
    relatedSlugs: ['home-lifestyle-ugc-playbook', 'furniture-ugc-playbook', 'shop-the-look-from-a-customer-photo'],
    term: 'Mood board',
    cluster: 'vertical',
    shortAnswer:
      'A grid of visual references (Pinterest-style) showing the aesthetic direction of a space or look. Used by customers in pre-purchase research; brand-curated mood boards on PDPs lift dwell time +47%.',
    industries: ['home-lifestyle'],
    relatedTerms: ['styling-photo', 'lookbook'],
  },
  {
    slug: 'size-scale-visualisation',
    relatedSlugs: ['furniture-ugc-playbook', 'shoppable-video-furniture', 'reducing-returns-with-pre-purchase-ugc'],
    term: 'Size / scale visualisation',
    cluster: 'vertical',
    shortAnswer:
      'A photo showing a furniture piece scaled against a person or familiar object. Solves the "is it actually that big?" question that kills high-AOV furniture conversion and drives returns.',
    industries: ['furniture'],
    relatedTerms: ['in-your-room-photo'],
  },

  // Hospitality
  {
    slug: 'guest-review',
    relatedSlugs: ['hospitality-ugc-playbook', 'travel-and-experiences-ugc-playbook', 'photo-video-reviews-vs-text-reviews'],
    term: 'Guest review',
    cluster: 'vertical',
    shortAnswer:
      'A review left by a hotel, restaurant or venue guest. The dominant UGC format for hospitality, often with photos. Guest UGC outconverts glossy hero images by 30-47%.',
    industries: ['hospitality'],
    relatedTerms: ['property-tour-ugc', 'stay-with-us'],
  },
  {
    slug: 'property-tour-ugc',
    relatedSlugs: ['hospitality-ugc-playbook', 'travel-and-experiences-ugc-playbook', 'shoppable-video-vs-photography'],
    term: 'Property tour UGC',
    cluster: 'vertical',
    shortAnswer:
      'Customer-shot video walking through a hotel room, suite or venue. Authentic counter-balance to brand-shot "perfect light" property photography that buyers no longer trust at face value.',
    industries: ['hospitality'],
    relatedTerms: ['guest-review', 'stay-with-us'],
  },
  {
    slug: 'stay-with-us',
    relatedSlugs: ['hospitality-ugc-playbook', 'travel-and-experiences-ugc-playbook', 'tiktok-ugc-strategy-for-ecommerce'],
    term: '"Stay with us" video',
    cluster: 'vertical',
    shortAnswer:
      'A creator or guest documenting their stay as a short video, often a Reel or TikTok. High-velocity inbound source for boutique hospitality brands.',
    industries: ['hospitality'],
    relatedTerms: ['guest-review', 'property-tour-ugc', 'reel'],
  },

  // Electronics
  {
    slug: 'unboxing',
    relatedSlugs: ['consumer-electronics-ugc-playbook', 'what-is-ugc-and-why-it-converts', 'tiktok-ugc-strategy-for-ecommerce'],
    term: 'Unboxing',
    cluster: 'vertical',
    shortAnswer:
      'The genre of opening a product on camera, often shot in one take. Highest-engagement UGC format for consumer electronics; closely tied to first-impression conversion lift on PDPs.',
    industries: ['consumer-electronics'],
    relatedTerms: ['demo-video', 'setup-video', 'reel'],
  },
  {
    slug: 'setup-video',
    relatedSlugs: ['consumer-electronics-ugc-playbook', 'reducing-returns-with-pre-purchase-ugc', 'best-shoppable-video-fashion'],
    term: 'Setup / configuration video',
    cluster: 'vertical',
    aliases: ['setup tutorial', 'configuration walkthrough'],
    shortAnswer:
      'A creator walking through getting a device working out-of-the-box. Closes the "will I be able to make this work" anxiety that kills electronics conversion.',
    industries: ['consumer-electronics'],
    relatedTerms: ['unboxing', 'demo-video'],
  },
  {
    slug: 'demo-video',
    relatedSlugs: ['consumer-electronics-ugc-playbook', 'shoppable-video-vs-product-video', 'what-is-shoppable-video'],
    term: 'Demo / use-case video',
    cluster: 'vertical',
    shortAnswer:
      'A video showing the product actually being used (smart speaker playing music, drone in flight, keyboard being typed on). Required signal for considered-electronics purchases.',
    industries: ['consumer-electronics'],
    relatedTerms: ['unboxing', 'setup-video'],
  },

  // Sport
  {
    slug: 'gear-review',
    relatedSlugs: ['sporting-goods-ugc-playbook', 'consumer-electronics-ugc-playbook', 'photo-video-reviews-vs-text-reviews'],
    term: 'Gear review',
    cluster: 'vertical',
    shortAnswer:
      'A creator\'s honest assessment of sport, outdoor or fitness gear after real use (km logged, climbs done, runs run). Carries credibility because the gear has been tested under load.',
    industries: ['sporting-goods'],
    relatedTerms: ['activity-content', 'demo-video'],
  },
  {
    slug: 'activity-content',
    relatedSlugs: ['sporting-goods-ugc-playbook', 'ugc-for-athleisure', 'travel-and-experiences-ugc-playbook'],
    term: 'Activity content',
    cluster: 'vertical',
    aliases: ['training log', 'race-day video'],
    shortAnswer:
      'Customer-shot content of the activity the gear enables: trail-running clips, race-day Reels, climbing-sends videos. UGC source most likely to capture the gear in context.',
    industries: ['sporting-goods'],
    relatedTerms: ['gear-review', 'reel'],
  },
]

export const glossarySlugs = GLOSSARY.map((e) => e.slug)
export const glossarySlugSet = new Set(glossarySlugs)

export function findGlossaryEntry(slug: string): GlossaryEntry | null {
  return GLOSSARY.find((e) => e.slug === slug) || null
}

export function findGlossaryByCluster(cluster: GlossaryCluster): GlossaryEntry[] {
  return GLOSSARY.filter((e) => e.cluster === cluster)
}

// Cluster -> CSS accent class. Used both in the listing chips and on
// the per-term hero. Keeps the visual cue consistent.
export const CLUSTER_ACCENT: Record<GlossaryCluster, { dot: string; soft: string; chip: string }> = {
  'ugc-ops':         { dot: 'bg-primary',        soft: 'bg-primary/8',        chip: 'bg-primary/10 text-primary' },
  'shoppable-video': { dot: 'bg-foreground',     soft: 'bg-foreground/5',     chip: 'bg-foreground/10 text-foreground' },
  'aeo':             { dot: 'bg-sky-500',        soft: 'bg-sky-500/8',        chip: 'bg-sky-500/10 text-sky-700' },
  'legal':           { dot: 'bg-amber-500',      soft: 'bg-amber-500/8',      chip: 'bg-amber-500/10 text-amber-800' },
  'metrics':         { dot: 'bg-slate-500',      soft: 'bg-slate-500/8',      chip: 'bg-slate-500/10 text-slate-700' },
  'platforms':       { dot: 'bg-emerald-500',    soft: 'bg-emerald-500/8',    chip: 'bg-emerald-500/10 text-emerald-700' },
  'platform-ui':     { dot: 'bg-violet-500',     soft: 'bg-violet-500/8',     chip: 'bg-violet-500/10 text-violet-700' },
  'vertical':        { dot: 'bg-rose-500',       soft: 'bg-rose-500/8',       chip: 'bg-rose-500/10 text-rose-700' },
}
